import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import {
  Loader2, Plus, X, Search, Trash2, Pencil, Sparkles,
  Flame, CircleDot, PauseCircle, CheckCircle2, Lightbulb, Archive,
  ArrowRight, ExternalLink
} from 'lucide-react';

/**
 * ProjectBacklog — "Proje Mezarlığı"
 * Yarım kalmış / rafa kalkmış / unutulmuş işleri tek ekranda topla & ayağa kaldır.
 * Route: /hq/backlog (ProtectedRoute allowEmails — sadece sahibine açık)
 */

const STATUSES = [
  { key: 'idea',        label: 'Fikir',        icon: Lightbulb,    color: 'amber',   ring: 'ring-amber-200' },
  { key: 'in_progress', label: 'Devam Ediyor', icon: CircleDot,    color: 'blue',    ring: 'ring-blue-200' },
  { key: 'stalled',     label: 'Yarım Kaldı',  icon: PauseCircle,  color: 'rose',    ring: 'ring-rose-200' },
  { key: 'shipped',     label: 'Canlıda',      icon: CheckCircle2, color: 'emerald', ring: 'ring-emerald-200' },
  { key: 'abandoned',   label: 'Rafa Kalktı',  icon: Archive,      color: 'gray',    ring: 'ring-gray-200' },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.key, s]));

const PRIORITIES = [
  { key: 'high',   label: 'Yüksek', dot: 'bg-red-500' },
  { key: 'medium', label: 'Orta',   dot: 'bg-amber-500' },
  { key: 'low',    label: 'Düşük',  dot: 'bg-gray-400' },
];

const COLOR_BG = {
  amber:   'bg-amber-50 border-amber-200 text-amber-700',
  blue:    'bg-blue-50 border-blue-200 text-blue-700',
  rose:    'bg-rose-50 border-rose-200 text-rose-700',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  gray:    'bg-gray-50 border-gray-200 text-gray-600',
};

const EMPTY = {
  title: '', category: 'Diğer', area: 'both', status: 'stalled',
  priority: 'medium', description: '', next_action: '', revive: false, links: [],
};

export default function ProjectBacklog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [reviveOnly, setReviveOnly] = useState(false);

  const [editing, setEditing] = useState(null); // object or null
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get('/project-backlog');
      setItems(data.items || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/project-backlog/seed');
      await load();
      alert(`${data.inserted} proje hafızadan eklendi.`);
    } catch (e) {
      alert('Seed hatası: ' + (e.response?.data?.error || e.message));
    } finally {
      setSeeding(false);
    }
  };

  const openNew = () => { setEditing({ ...EMPTY }); setShowModal(true); };
  const openEdit = (it) => { setEditing({ ...it, links: it.links || [] }); setShowModal(true); };

  const save = async () => {
    if (!editing.title?.trim()) { alert('Başlık gerekli'); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/project-backlog/${editing.id}`, editing);
      } else {
        await api.post('/project-backlog', editing);
      }
      setShowModal(false); setEditing(null);
      await load();
    } catch (e) {
      alert('Kayıt hatası: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const patch = async (it, fields) => {
    // optimistic
    setItems(prev => prev.map(p => p.id === it.id ? { ...p, ...fields } : p));
    try {
      await api.put(`/project-backlog/${it.id}`, fields);
    } catch (e) {
      load(); // revert from server
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`"${it.title}" silinsin mi?`)) return;
    setItems(prev => prev.filter(p => p.id !== it.id));
    try { await api.delete(`/project-backlog/${it.id}`); }
    catch (e) { load(); }
  };

  const categories = useMemo(() => {
    const s = new Set(items.map(i => i.category).filter(Boolean));
    return ['all', ...Array.from(s).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter(i => {
      if (reviveOnly && !i.revive) return false;
      if (catFilter !== 'all' && i.category !== catFilter) return false;
      if (ql && !(`${i.title} ${i.description} ${i.next_action} ${i.category}`.toLowerCase().includes(ql))) return false;
      return true;
    });
  }, [items, q, catFilter, reviveOnly]);

  const byStatus = useMemo(() => {
    const m = {}; STATUSES.forEach(s => m[s.key] = []);
    filtered.forEach(i => { (m[i.status] || (m[i.status] = [])).push(i); });
    return m;
  }, [filtered]);

  const stats = useMemo(() => ({
    total: items.length,
    revive: items.filter(i => i.revive).length,
    open: items.filter(i => ['idea', 'in_progress', 'stalled'].includes(i.status)).length,
    shipped: items.filter(i => i.status === 'shipped').length,
  }), [items]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[1500px] mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-red-500" /> Proje Mezarlığı
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Yarım kalan, rafa kalkan, unutulan işler — tek ekranda topla ve ayağa kaldır.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {items.length === 0 && (
              <button onClick={seed} disabled={seeding}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
                {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Hafızadan Doldur
              </button>
            )}
            <button onClick={openNew}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">
              <Plus className="w-4 h-4" /> Yeni Proje
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <StatCard label="Toplam" value={stats.total} />
          <StatCard label="Açık (fikir/devam/yarım)" value={stats.open} tone="blue" />
          <StatCard label="Ayağa Kaldırılacak" value={stats.revive} tone="rose" />
          <StatCard label="Canlıda" value={stats.shipped} tone="emerald" />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ara…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-red-200 focus:outline-none" />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white">
            {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'Tüm kategoriler' : c}</option>)}
          </select>
          <button onClick={() => setReviveOnly(v => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${reviveOnly ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-700 border-gray-200'}`}>
            <Flame className="w-4 h-4" /> Sadece ayağa kaldırılacak
          </button>
        </div>

        {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            Henüz proje yok. <button onClick={seed} className="text-red-600 font-medium underline">Hafızadan doldur</button> ya da yeni ekle.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            {STATUSES.map(s => {
              const Icon = s.icon;
              const col = byStatus[s.key] || [];
              return (
                <div key={s.key} className="bg-white rounded-xl border border-gray-200 p-3 flex flex-col">
                  <div className={`flex items-center justify-between mb-3 px-2 py-1.5 rounded-lg border ${COLOR_BG[s.color]}`}>
                    <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                      <Icon className="w-4 h-4" /> {s.label}
                    </span>
                    <span className="text-xs font-bold">{col.length}</span>
                  </div>
                  <div className="space-y-2.5 flex-1">
                    {col.map(it => (
                      <Card key={it.id} it={it} onEdit={openEdit} onRemove={remove} onPatch={patch} />
                    ))}
                    {col.length === 0 && <p className="text-[11px] text-gray-300 text-center py-4">—</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && editing && (
        <Modal editing={editing} setEditing={setEditing} onClose={() => { setShowModal(false); setEditing(null); }} onSave={save} saving={saving} />
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'gray' }) {
  const tones = {
    gray: 'bg-white border-gray-200 text-gray-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[11px] uppercase tracking-wide opacity-70 mt-0.5">{label}</div>
    </div>
  );
}

function Card({ it, onEdit, onRemove, onPatch }) {
  const navigate = useNavigate();
  const prio = PRIORITIES.find(p => p.key === it.priority) || PRIORITIES[1];
  const links = Array.isArray(it.links) ? it.links : [];
  const internalRoute = links.find(l => typeof l === 'string' && l.startsWith('/'));
  const externalLinks = links.filter(l => typeof l === 'string' && /^https?:\/\//.test(l));
  return (
    <div className={`group rounded-lg border p-3 hover:shadow-sm transition ${it.revive ? 'border-rose-300 bg-rose-50/40' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${prio.dot}`} title={`Öncelik: ${prio.label}`} />
          <h3 className="text-sm font-semibold text-gray-900 leading-snug">{it.title}</h3>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
          <button onClick={() => onPatch(it, { revive: !it.revive })} title="Ayağa kaldır işareti"
            className={`p-1 rounded ${it.revive ? 'text-rose-600' : 'text-gray-400 hover:text-rose-600'}`}>
            <Flame className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onEdit(it)} className="p-1 rounded text-gray-400 hover:text-gray-700"><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={() => onRemove(it)} className="p-1 rounded text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">{it.category}</span>
        {it.area && it.area !== 'both' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">{it.area}</span>}
      </div>

      {it.description && <p className="text-[11px] text-gray-500 mt-2 line-clamp-3">{it.description}</p>}
      {it.next_action && it.next_action !== '—' && (
        <div className="mt-2 text-[11px] text-gray-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">
          <span className="font-semibold text-amber-700">Sıradaki:</span> {it.next_action}
        </div>
      )}

      {(internalRoute || externalLinks.length > 0) && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {internalRoute && (
            <button onClick={() => navigate(internalRoute)}
              className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md bg-gray-900 text-white hover:bg-gray-700">
              Sayfaya Git <ArrowRight className="w-3 h-3" />
            </button>
          )}
          {externalLinks.map((l, i) => (
            <a key={i} href={l} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50">
              <ExternalLink className="w-3 h-3" /> Link
            </a>
          ))}
        </div>
      )}

      {/* quick status move */}
      <div className="mt-2 flex flex-wrap gap-1">
        {STATUSES.filter(s => s.key !== it.status).map(s => (
          <button key={s.key} onClick={() => onPatch(it, { status: s.key })}
            className="text-[9px] px-1.5 py-0.5 rounded border border-gray-200 text-gray-400 hover:text-gray-700 hover:border-gray-300">
            → {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Modal({ editing, setEditing, onClose, onSave, saving }) {
  const set = (k, v) => setEditing(e => ({ ...e, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">{editing.id ? 'Projeyi Düzenle' : 'Yeni Proje'}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <Field label="Başlık">
            <input value={editing.title} onChange={e => set('title', e.target.value)} className={inputCls} autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kategori">
              <input value={editing.category} onChange={e => set('category', e.target.value)} className={inputCls} placeholder="Email/SES, Rakip…" />
            </Field>
            <Field label="Alan">
              <select value={editing.area} onChange={e => set('area', e.target.value)} className={inputCls}>
                <option value="both">both</option><option value="frontend">frontend</option>
                <option value="backend">backend</option><option value="ops">ops</option><option value="data">data</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Durum">
              <select value={editing.status} onChange={e => set('status', e.target.value)} className={inputCls}>
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Öncelik">
              <select value={editing.priority} onChange={e => set('priority', e.target.value)} className={inputCls}>
                {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Açıklama">
            <textarea value={editing.description} onChange={e => set('description', e.target.value)} rows={3} className={inputCls} />
          </Field>
          <Field label="Sıradaki Aksiyon">
            <input value={editing.next_action} onChange={e => set('next_action', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Linkler (virgülle ayır)">
            <input
              value={(editing.links || []).join(', ')}
              onChange={e => set('links', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className={inputCls} placeholder="/budget/…, https://…" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={!!editing.revive} onChange={e => set('revive', e.target.checked)} className="rounded text-rose-600" />
            <Flame className="w-4 h-4 text-rose-500" /> Ayağa kaldırılacak (öne çıkar)
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50">İptal</button>
          <button onClick={onSave} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-red-200 focus:outline-none';
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
